import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { getRouter } from "@/services/router/router";

const DATE_FORMAT = "dd/MM/yyyy";
const TIME_FORMAT = "HH:mm";
const SKELETON_ROWS = ["alpha", "bravo", "charlie", "delta"];

const formatCurrency = (value: number) =>
	new Intl.NumberFormat("pt-PT", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 2,
	}).format(value);

const normalizeNumber = (value: number | null | undefined) => {
	const parsed = Number(value ?? 0);
	return Number.isNaN(parsed) ? 0 : parsed;
};

type MaterialSummary = {
	brand?: string | null;
	model?: string | null;
	description?: string | null;
	storeBox?: string | null;
	price?: number | null;
};

type RepairSummary = {
	_id?: string | null;
};

type StockRequestEntry = {
	id?: string | number | null;
	requestNumber?: string | number | null;
	createdAt?: string | Date | null;
	material: MaterialSummary;
	quantity?: number | null;
	storeBox?: string | null;
	price?: number | null;
	observation?: string | null;
	state?: string | null;
	repair?: RepairSummary | null;
};

type StockRequestsListProps = {
	data: StockRequestEntry[];
	loading?: boolean;
	isAttendance: boolean;
	canEditRequests: boolean;
	onProcessRequest: (entry: StockRequestEntry) => void;
	onRemoveRequest: (entry: StockRequestEntry) => void;
	onDeliverMaterial: (entry: StockRequestEntry) => void;
	onPutMaterialOnStock: (entry: StockRequestEntry) => void;
};

const buildMaterialLabel = (material: MaterialSummary) => {
	const brand = material.brand?.trim() ?? "";
	const model = material.model?.trim() ?? "";
	const description = material.description?.trim() ?? "";
	const parts = [brand, model, description].filter((value) => value.length > 0);
	return parts.join(" ") || "-";
};

const formatDate = (
	value: string | Date | null | undefined,
	formatString: string,
) => {
	if (!value) {
		return "-";
	}

	const date = typeof value === "string" ? new Date(value) : value;
	if (Number.isNaN(date.getTime())) {
		return "-";
	}

	return format(date, formatString);
};

export function StockRequestsList({
	data,
	loading = false,
	isAttendance,
	canEditRequests,
	onProcessRequest,
	onRemoveRequest,
	onDeliverMaterial,
	onPutMaterialOnStock,
}: StockRequestsListProps) {
	const canShowActions = isAttendance || canEditRequests;

	const handleShowRepair = async (repair?: RepairSummary | null) => {
		if (!repair?._id) {
			return;
		}

		const router = await getRouter();
		router.push({ name: "repair.single", params: { id: repair._id } });
	};

	const renderSkeleton = () => (
		<TableBody>
			{SKELETON_ROWS.map((rowKey) => (
				<TableRow
					key={`stock-requests-skeleton-${rowKey}`}
					className="animate-pulse"
				>
					<TableCell>
						<div className="h-4 w-16 rounded bg-muted/70" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-24 rounded bg-muted/60" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-24 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-48 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-10 rounded bg-muted/40" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-12 rounded bg-muted/40" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-20 rounded bg-muted/30" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-28 rounded bg-muted/30" />
					</TableCell>
					{canShowActions ? (
						<TableCell className="text-right">
							<div className="ml-auto h-8 w-32 rounded bg-muted/30" />
						</TableCell>
					) : null}
				</TableRow>
			))}
		</TableBody>
	);

	const renderEmpty = () => (
		<TableBody>
			<TableRow>
				<TableCell
					colSpan={canShowActions ? 9 : 8}
					className="py-10 text-center text-sm text-muted-foreground"
				>
					Sem pedidos de stock.
				</TableCell>
			</TableRow>
		</TableBody>
	);

	return (
		<section className="stock-requests-list">
			<div className="material-requests-list material-requests-list--client">
				<div className="overflow-hidden rounded-lg border border-border/60 bg-card/70">
					<Table>
						<TableHeader>
							<TableRow className="bg-muted/50">
								<TableHead className="w-[90px] text-center">#Pedido</TableHead>
								<TableHead>Data</TableHead>
								<TableHead>Hora</TableHead>
								<TableHead>Descrição</TableHead>
								<TableHead className="w-[80px] text-center">Qtn.</TableHead>
								<TableHead className="w-[90px] text-center">Caixa</TableHead>
								<TableHead className="w-[140px] text-center">
									Preço unidade
								</TableHead>
								<TableHead>Observação</TableHead>
								{canShowActions ? (
									<TableHead className="w-[240px] text-right" />
								) : null}
							</TableRow>
						</TableHeader>
						{loading ? (
							renderSkeleton()
						) : data.length > 0 ? (
							<TableBody>
								{data.map((entry) => {
									const rowKey = entry.id ?? entry.requestNumber ?? "stock-row";
									const showActions =
										entry.state !== "waiting_delivery" && !isAttendance;
									const priceValue = entry.price ?? entry.material.price ?? 0;

									return (
										<TableRow key={rowKey}>
											<TableCell className="text-center">
												{entry.requestNumber ?? "-"}
											</TableCell>
											<TableCell>
												{formatDate(entry.createdAt, DATE_FORMAT)}
											</TableCell>
											<TableCell>
												{formatDate(entry.createdAt, TIME_FORMAT)}
											</TableCell>
											<TableCell>
												{buildMaterialLabel(entry.material)}
											</TableCell>
											<TableCell className="text-center">
												{entry.quantity ?? "-"}
											</TableCell>
											<TableCell className="text-center">
												{entry.material.storeBox ?? "-"}
											</TableCell>
											<TableCell className="text-center">
												{formatCurrency(normalizeNumber(priceValue))}
											</TableCell>
											<TableCell>{entry.observation ?? "-"}</TableCell>
											{canShowActions ? (
												<TableCell className="text-right">
													<div className="flex justify-end gap-2">
														{entry.repair ? (
															<Button
																variant="secondary"
																size="sm"
																onClick={() => handleShowRepair(entry.repair)}
															>
																Reparação
															</Button>
														) : null}
														{showActions ? (
															<>
																<Button
																	size="sm"
																	onClick={() => onProcessRequest(entry)}
																>
																	Comprar
																</Button>
																<Button
																	variant="destructive"
																	size="sm"
																	onClick={() => onRemoveRequest(entry)}
																>
																	Cancelar
																</Button>
															</>
														) : null}
														{!showActions ? (
															<>
																{(isAttendance && !entry.repair) ||
																canEditRequests ? (
																	<Button
																		size="sm"
																		onClick={() => onDeliverMaterial(entry)}
																	>
																		Entregar
																	</Button>
																) : null}
																{!isAttendance ? (
																	<Button
																		variant="secondary"
																		size="sm"
																		onClick={() => onPutMaterialOnStock(entry)}
																	>
																		Colocar no stock
																	</Button>
																) : null}
															</>
														) : null}
													</div>
												</TableCell>
											) : null}
										</TableRow>
									);
								})}
							</TableBody>
						) : (
							renderEmpty()
						)}
					</Table>
				</div>
			</div>
		</section>
	);
}
